import { ref, computed } from "vue";

export function useColumnFilter({
  activeFilters,
  filterOptions,
  t,
  filterPopoverRef,
  emit,
  formatLabel,
}) {
  const activeFilterColumn = ref(null);
  const textFilterOperator = ref("contains");
  const textFilterValue = ref("");
  const dateFilterFrom = ref(null);
  const dateFilterTo = ref(null);

  const textFilterOperators = computed(() => [
    { label: t("filter.contains"), value: "contains" },
    { label: t("filter.notContains"), value: "notContains" },
  ]);

  const currentFilterOptions = computed(() => {
    if (!activeFilterColumn.value) {
      return [];
    }
    const field = getFilterField(activeFilterColumn.value);
    if (activeFilterColumn.value.filterType === "boolean") {
      return [
        { label: t("yes"), value: "true" },
        { label: t("no"), value: "false" },
      ];
    }
    const format = activeFilterColumn.value.filterFormat || activeFilterColumn.value.format;
    return (filterOptions.value[field] || []).map((v) => ({
      label: format && formatLabel ? formatLabel(v, format) : v,
      value: v,
    }));
  });

  const hasActiveFilters = computed(() => {
    return Object.values(activeFilters.value).some((v) => {
      if (Array.isArray(v)) {
        return v.length > 0;
      }
      if (v && typeof v === "object") {
        // Date filter: { from, to }
        if ("from" in v || "to" in v) {
          return !!v.from || !!v.to;
        }
        // Text filter: { operator, value }
        return !!v.value;
      }
      return false;
    });
  });

  function clearAllFilters() {
    activeFilterColumn.value = null;
    textFilterOperator.value = "contains";
    textFilterValue.value = "";
    emit("clear-all-filters");
  }

  function getFilterField(col) {
    return col.filterField || col.sortField;
  }

  function isColumnFiltered(col) {
    const field = getFilterField(col);
    const filter = activeFilters.value[field];
    if (Array.isArray(filter)) {
      return filter.length > 0;
    }
    if (filter && typeof filter === "object") {
      if ("from" in filter || "to" in filter) {
        return !!filter.from || !!filter.to;
      }
      return !!filter.value;
    }
    return false;
  }

  function isOptionSelected(value) {
    if (!activeFilterColumn.value) {
      return false;
    }
    const field = getFilterField(activeFilterColumn.value);
    const selected = activeFilters.value[field] || [];
    return Array.isArray(selected) && selected.includes(value);
  }

  function openFilterPopover(event, col) {
    activeFilterColumn.value = col;
    const field = getFilterField(col);
    const existing = activeFilters.value[field];
    if (col.filterType === "text") {
      if (
        existing &&
        typeof existing === "object" &&
        !Array.isArray(existing)
      ) {
        textFilterOperator.value = existing.operator || "contains";
        textFilterValue.value = existing.value || "";
      } else {
        textFilterOperator.value = "contains";
        textFilterValue.value = "";
      }
    } else if (col.filterType === "date") {
      if (
        existing &&
        typeof existing === "object" &&
        !Array.isArray(existing)
      ) {
        dateFilterFrom.value = existing.from
          ? parseLocalDate(existing.from)
          : null;
        dateFilterTo.value = existing.to ? parseLocalDate(existing.to) : null;
      } else {
        dateFilterFrom.value = null;
        dateFilterTo.value = null;
      }
    }
    filterPopoverRef.value.toggle(event);
  }

  function toggleFilterOption(value) {
    const field = getFilterField(activeFilterColumn.value);
    const current = [...(activeFilters.value[field] || [])];
    const idx = current.indexOf(value);
    if (idx >= 0) {
      current.splice(idx, 1);
    } else {
      current.push(value);
    }
    emit("filter-change", {
      field,
      values: current.length > 0 ? current : null,
    });
  }

  function applyTextFilter() {
    const field = getFilterField(activeFilterColumn.value);
    if (!textFilterValue.value.trim()) {
      emit("filter-change", { field, values: null });
    } else {
      emit("filter-change", {
        field,
        values: {
          operator: textFilterOperator.value,
          value: textFilterValue.value.trim(),
        },
      });
    }
    filterPopoverRef.value.hide();
  }

  /**
   * Parse an ISO date string (e.g. "2026-06-15T00:00:00Z") into a local Date
   * using only the date part, ignoring the time/timezone to avoid day shift.
   */
  function parseLocalDate(isoString) {
    const [datePart] = isoString.split("T");
    const [y, m, d] = datePart.split("-").map(Number);
    return new Date(y, m - 1, d);
  }

  /**
   * Build an ISO date string using the local date parts (year, month, day)
   * to avoid timezone offset shifting the day in UTC.
   */
  function toLocalDateISO(date, endOfDay) {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return endOfDay ? `${y}-${m}-${d}T23:59:59Z` : `${y}-${m}-${d}T00:00:00Z`;
  }

  function applyDateFilter() {
    const field = getFilterField(activeFilterColumn.value);
    if (!dateFilterFrom.value && !dateFilterTo.value) {
      emit("filter-change", { field, values: null });
    } else {
      emit("filter-change", {
        field,
        values: {
          from: dateFilterFrom.value
            ? toLocalDateISO(dateFilterFrom.value, false)
            : null,
          to: dateFilterTo.value
            ? toLocalDateISO(dateFilterTo.value, true)
            : null,
        },
      });
    }
    filterPopoverRef.value.hide();
  }

  function clearColumnFilter() {
    const field = getFilterField(activeFilterColumn.value);
    emit("filter-change", { field, values: null });
    if (activeFilterColumn.value.filterType === "text") {
      textFilterValue.value = "";
      textFilterOperator.value = "contains";
    } else if (activeFilterColumn.value.filterType === "date") {
      dateFilterFrom.value = null;
      dateFilterTo.value = null;
    }
    filterPopoverRef.value.hide();
  }

  return {
    activeFilterColumn,
    textFilterOperator,
    textFilterValue,
    textFilterOperators,
    dateFilterFrom,
    dateFilterTo,
    currentFilterOptions,
    hasActiveFilters,
    clearAllFilters,
    getFilterField,
    isColumnFiltered,
    isOptionSelected,
    openFilterPopover,
    toggleFilterOption,
    applyTextFilter,
    applyDateFilter,
    clearColumnFilter,
  };
}
